import { KeyValuePipe } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { TooltipDirective } from '@cumlaude/shared-components-overlays';
import { ConversationTurnWithFeedback, DashboardInfo, ModelCallDebug, RAIChatOpmerking } from '@cumlaude/service-contract';
import { formatCompact, formatDecimal } from '@cumlaude/shared-pipes';
import { DASHBOARD_REGISTRY, findDashboardOptionsByName, isOptionValueAllowed, RegistryNode } from '@cumlaude/shared-dashboard-registry';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { ChatOpmerkingenComponent } from './chat-opmerkingen.component';
import { MarkdownService } from './markdown.service';
import { vertaalNaarHistorie } from './schooljaar-historie';

export type DashboardURLComponents = {
	url: string[];
	queryParams: { [key: string]: any };
};

@Component({
	selector: 'app-chat-turns',
	standalone: true,
	templateUrl: './chat-turns.component.html',
	styleUrls: ['./chat-turns.component.scss'],
	imports: [TooltipDirective, IconComponent, ChatOpmerkingenComponent, KeyValuePipe],
})
export class ChatTurnsComponent {
	readonly turns = input<ConversationTurnWithFeedback[]>();

	/** Inspect mode: readonly and details visible */
	readonly inspect = input(false);

	/**
	 * Wat het model op dit moment doet, in de volgorde waarin de backend het meldde. Alleen gevuld
	 * terwijl er op een antwoord gewacht wordt; de meldingen worden niet bij de turn bewaard.
	 */
	readonly progressMessages = input<string[]>([]);

	/**
	 * Admin-notities bij deze conversatie. De opmerkingen-UI hangt aan de aanwezigheid van deze input
	 * en bewust niet aan `inspect`: die betekent "readonly en details zichtbaar", terwijl een admin
	 * hier juist wél schrijft. Wie niets meegeeft (de user-app) krijgt de UI ook niet te zien.
	 */
	readonly opmerkingen = input<RAIChatOpmerking[] | undefined>(undefined);

	/**
	 * Naam van de gebruiker die de conversatie voerde, getoond voor zijn feedback zodat die net als
	 * een notitie een afzender heeft. Alleen zichtbaar in de admin-weergave: in de user-app weet de
	 * gebruiker zelf wel wie hij is.
	 */
	readonly gebruiker = input<string | undefined>(undefined);

	feedback = output<{ index: number; sentiment: 1 | -1 }>();
	goToDashboard = output<DashboardURLComponents>();
	opmerkingToevoegen = output<number>();
	opmerkingWijzigen = output<RAIChatOpmerking>();
	opmerkingVerwijderen = output<RAIChatOpmerking>();

	readonly toontOpmerkingen = computed(() => this.opmerkingen() !== undefined);

	/** Opmerkingen per turn, zodat de template niet bij elke change detection opnieuw filtert. */
	private readonly opmerkingenPerTurn = computed(() => {
		const perTurn = new Map<number, RAIChatOpmerking[]>();
		for (const opmerking of this.opmerkingen() ?? []) {
			if (opmerking.turnIndex == null) continue;
			const bestaande = perTurn.get(opmerking.turnIndex);
			if (bestaande) bestaande.push(opmerking);
			else perTurn.set(opmerking.turnIndex, [opmerking]);
		}
		return perTurn;
	});

	opmerkingenVoorTurn(index: number): RAIChatOpmerking[] {
		return this.opmerkingenPerTurn().get(index) ?? [];
	}

	private readonly markdown = inject(MarkdownService);

	/** Converteert markdown naar html; zie {@link MarkdownService} voor de cache en het async-geval. */
	parseMarkdown(text: string): string {
		return this.markdown.parse(text);
	}

	formatFilters(filters?: { field: string; values: string[] }[] | null): string[] {
		return filters?.map((f) => `${f.field} = ${f.values.join(', ')}`) || [];
	}

	/**
	 * De regel met wat dit antwoord gekost heeft. In TS opgebouwd in plaats van met pipes in de
	 * template: het zijn zes getallen op één regel, en dat leest hier stuk beter.
	 *
	 * De aantallen zijn compact (`27,5k`), want deze regel wordt gescand en niet nagerekend. Het
	 * exacte getal staat in de `title`, zie {@link exacteAantallen}. `durationMs` blijft exact, met
	 * duizendtalscheiding: bij een duur is het verschil tussen 400 en 900 ms juist wat je wil zien.
	 */
	aanroepRegel(debug: ModelCallDebug): string {
		const { input, cached, output, reasoning, total } = debug.tokens;
		return [
			debug.model,
			`${debug.rounds} ${debug.rounds === 1 ? 'ronde' : 'ronden'}`,
			`${formatDecimal(debug.durationMs, '1.0-0')} ms`,
			`input ${formatCompact(input)} (cached ${formatCompact(cached)})`,
			`output ${formatCompact(output)} (reasoning ${formatCompact(reasoning)})`,
			`totaal ${formatCompact(total)}`,
		].join(' · ');
	}

	/**
	 * Welke domeinschema's vooraf zijn meegegeven en welke het model zelf heeft opgevraagd. Samen
	 * vertellen die of het vooraf meegeven zijn werk doet: een preload die het model alsnog opvraagt is
	 * dubbel betaald.
	 */
	herkomstRegel(debug: ModelCallDebug): string | undefined {
		const delen = [];
		if (debug.preloadedDomains?.length) delen.push(`preload: ${debug.preloadedDomains.join(', ')}`);
		if (debug.toolCalls?.length) delen.push(`tool calls: ${debug.toolCalls.join(', ')}`);
		const kennisbank = debug.kennisbank;
		if (kennisbank) {
			// De duur staat er apart bij en niet in `aanroepRegel`: die telt alleen de aanroepen van
			// het model zelf, terwijl dit vaak het grootste deel van de wachttijd is.
			const mislukt = kennisbank.mislukt ? ', mislukt' : '';
			delen.push(
				`kennisbank ${kennisbank.type}: ${formatCompact(kennisbank.tokens)} tokens, ${formatDecimal(kennisbank.durationMs, '1.0-0')} ms${mislukt}`
			);
		}
		if (debug.jsonHerstelPogingen) {
			delen.push(`JSON-herstel: ${debug.jsonHerstelPogingen}`);
		}
		return delen.length ? delen.join(' · ') : undefined;
	}

	/** De exacte aantallen, voor de `title`: compact afronden verliest precies het verschil dat je bij het beoordelen van de caching soms wél wil zien. */
	exacteAantallen(debug: ModelCallDebug): string {
		const { input, cached, output, reasoning, total } = debug.tokens;
		return `input ${input}, cached ${cached}, output ${output}, reasoning ${reasoning}, totaal ${total}, duur ${debug.durationMs} ms`;
	}

	optionLabel(param: string): string {
		return OPTION_LABELS[param] ?? param;
	}

	goToDashboardClick(dashboard: DashboardInfo) {
		const dashboardURL = getDashboardURLComponents(dashboard);
		if (dashboardURL) {
			this.goToDashboard.emit(dashboardURL);
		}
	}
}

const OPTION_LABELS: Record<string, string> = {
	variant: 'Variant',
	sortOrder: 'Sortering',
	eenheid: 'Eenheid',
	aanwezigheiduitsplitsing: 'Uitsplitsing',
};

/**
 * Bouwt een lookup-map van dashboard-naam → URL-segmenten door de registry recursief te doorlopen.
 * Tussenknopen (met `children`) worden overgeslagen; alleen bladknopen met een `name` worden opgenomen.
 */
function buildNameToPathMap(nodes: ReadonlyArray<RegistryNode>, parentPath: string[] = []): Map<string, string[]> {
	const map = new Map<string, string[]>();
	for (const node of nodes) {
		const currentPath = [...parentPath, node.path];
		if (node.children) {
			for (const [name, path] of buildNameToPathMap(node.children, currentPath)) {
				map.set(name, path);
			}
		} else if (node.name) {
			map.set(node.name, currentPath);
		}
	}
	return map;
}

/**
 * Naam → URL-segmenten voor alle hoofd-dashboards.
 * De `details`-tak wordt uitgesloten: dat zijn drill-down panels die de AI niet rechtstreeks
 * navigeert, en hun namen overlappen met hoofd-dashboardnamen (bijv. 'Doorstroom', 'Onderbouwsnelheid').
 */
const DASHBOARD_NAME_TO_PATH = buildNameToPathMap(DASHBOARD_REGISTRY.filter((n) => n.path !== 'details'));

export function getDashboardURLComponents(dashboard: DashboardInfo | undefined): DashboardURLComponents | undefined {
	const dashboardName = dashboard?.dashboard;
	if (dashboardName) {
		const url = DASHBOARD_NAME_TO_PATH.get(dashboardName);
		if (!url) throw new Error('Unknown dashboard: ' + dashboardName);

		const g = dashboard.grouping?.join(':');
		// Het model verzint soms een param of waarde.
		const options = Object.fromEntries(
			findDashboardOptionsByName(dashboardName).flatMap((option) => {
				const waarde = dashboard.options?.[option.param];
				return waarde && isOptionValueAllowed(option, waarde, dashboardName) ? [[option.param, waarde]] : [];
			})
		);
		// Converteer de filter property (array van { field: string, values: any[] }) naar een object (key-value pairs)
		// waarbij de key de field is en de value een JSON-string van de values array, zodat we deze kunnen gebruiken in de URL.
		const historie =
			dashboard.options?.['variant'] === 'Historie' ? vertaalNaarHistorie(dashboard.filter) : { filter: dashboard.filter, queryParams: {} };
		const filters = Object.fromEntries(historie.filter?.map((f) => [f.field, JSON.stringify(f.values)]) || []);
		// Maak de DashboardURL aan
		return { url, queryParams: { g, ...options, ...filters, ...historie.queryParams } };
	}
	return undefined;
}
